/* Product add-on toggle.
   Adds or removes a real product line in the cart, then refreshes Dawn's cart
   sections in place. Deliberately does NOT call cart.renderContents(), because
   that opens the drawer — ticking an add-on shouldn't hijack the page. */
class PbxProductAddon extends HTMLElement {
  connectedCallback() {
    this.variantId = Number(this.dataset.variantId);
    this.button = this.querySelector('[data-pbx-addon-toggle]');
    this.label = this.querySelector('[data-pbx-addon-label]');
    if (!this.button || !this.variantId) return;

    this.button.addEventListener('click', () => this.toggle());

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Ignore our own publish, otherwise this loops
        if (event?.source === 'pbx-addon') return;
        this.refresh();
      });
    }

    this.refresh();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  get cartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  async refresh() {
    try {
      const cart = await (await fetch(`${routes.cart_url}.js`)).json();
      const line = cart.items.find((item) => item.variant_id === this.variantId);
      this.lineKey = line?.key || null;
      this.setState(Boolean(line));
    } catch (error) {
      // Leave the button in its current state rather than guessing
    }
  }

  setState(inCart) {
    this.inCart = inCart;
    this.classList.toggle('is-added', inCart);
    this.button.setAttribute('aria-pressed', String(inCart));
    if (this.label) {
      this.label.textContent = inCart ? this.dataset.addedLabel : this.dataset.addLabel;
    }
  }

  async toggle() {
    if (this.busy) return;
    this.busy = true;
    this.button.setAttribute('aria-disabled', 'true');
    this.classList.add('is-busy');

    try {
      const payload = this.inCart
        ? { id: this.lineKey, quantity: 0 }
        : { items: [{ id: this.variantId, quantity: 1 }] };
      const url = this.inCart ? `${routes.cart_change_url}` : `${routes.cart_add_url}`;

      const cart = this.cartElement;
      if (cart) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.status) throw new Error(data.description || data.message);

      this.updateCartSections(data.sections);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pbx-addon', cartData: data });
      }
      await this.refresh();
    } catch (error) {
      await this.refresh();
    } finally {
      this.busy = false;
      this.button.removeAttribute('aria-disabled');
      this.classList.remove('is-busy');
    }
  }

  /* Same section swap Dawn's cart does, minus the drawer opening */
  updateCartSections(sections) {
    const cart = this.cartElement;
    if (!cart || !sections) return;
    cart.getSectionsToRender().forEach((section) => {
      const element = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!element || !sections[section.id]) return;
      element.innerHTML = cart.getSectionInnerHTML(sections[section.id], section.selector);
    });
  }
}

/* The same add-on offered inside the cart drawer: add only, because the row
   removes itself — the drawer re-renders from Liquid, which skips products
   already in the cart. */
class PbxCartAddon extends HTMLElement {
  connectedCallback() {
    this.variantId = Number(this.dataset.variantId);
    this.button = this.querySelector('[data-pbx-cart-addon-toggle]');
    if (!this.button || !this.variantId) return;
    this.button.addEventListener('click', () => this.add());
  }

  get cartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  async add() {
    if (this.busy) return;
    this.busy = true;
    this.classList.add('is-busy');
    this.button.setAttribute('aria-disabled', 'true');

    const cart = this.cartElement;
    const body = { items: [{ id: this.variantId, quantity: 1 }] };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message);

      // The drawer is already open, so swap the sections in place rather than
      // calling renderContents, which would re-open and re-focus it.
      if (cart && data.sections) {
        cart.getSectionsToRender().forEach((section) => {
          const element = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!element || !data.sections[section.id]) return;
          element.innerHTML = cart.getSectionInnerHTML(data.sections[section.id], section.selector);
        });
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pbx-cart-addon', cartData: data });
      }
    } catch (error) {
      this.busy = false;
      this.classList.remove('is-busy');
      this.button.removeAttribute('aria-disabled');
    }
  }
}

/* Guarded: the add-on section prints this tag once per instance, and a page can
   carry several. */
if (!customElements.get('pbx-addon')) customElements.define('pbx-addon', PbxProductAddon);
if (!customElements.get('pbx-cart-addon')) customElements.define('pbx-cart-addon', PbxCartAddon);
